import { Text } from '@joor/design-system'
import styled from 'styled-components'

import {
  PremiumFeaturesQuery,
  SubscriptionStatus,
} from '__generated__/atlas-types'

import { Content } from './Content'
import { StatusOrAction } from './StatusOrAction'
import { SubscriptionsView } from 'features/Subscriptions/queries.hooks'
import { FeatureCardIds } from 'features/Subscriptions/subscriptions.ids'

// gives every feature information about the other features that
// belong to the same packages group
export type PackageStatus = { id: string; status?: SubscriptionStatus }

export type PremiumFeatureFromHook = NonNullable<
  PremiumFeaturesQuery['premiumFeatures']
>[0] & {
  packagesStatus?: PackageStatus[]
  subscriptionsView?: SubscriptionsView
}

const Card = styled.div`
  display: flex;
  width: 90%;
  align-items: stretch;
  border-radius: 10px;
  border: 1px solid var(--color-primary-200);

  @media (max-width: 1180px) {
    height: auto;
  }
`

const Status = styled.div`
  writing-mode: sideways-lr;
  padding: 0 10px;
  border-radius: 10px 0 0 10px;
  background-color: green;
  text-align: center;
  width: 40px;

  @media (max-width: 1180px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const statusesInfo: Record<
  SubscriptionStatus,
  { text: string; color: string }
> = {
  [SubscriptionStatus.AVAILABLE_FOR_PURCHASE]: {
    text: 'Available For Purchase',
    color: 'var(--color-secondary-300)',
  },
  [SubscriptionStatus.SCHEDULED]: {
    text: 'Scheduled',
    color: 'var(--color-warning-200)',
  },
  [SubscriptionStatus.PROCESSING]: {
    text: 'Processing Feature',
    color: 'var(--color-warning-200)',
  },
  [SubscriptionStatus.CANCELLED]: {
    text: 'Cancelled Subscription',
    color: 'var(--color-primary-200)',
  },
  [SubscriptionStatus.EXPIRED]: {
    text: 'Payment Failure',
    color: 'var(--color-error-200)',
  },
  [SubscriptionStatus.ACTIVE]: {
    text: 'Active Feature',
    color: 'var(--color-success-200)',
  },
  [SubscriptionStatus.ERROR]: {
    text: 'Error',
    color: 'var(--color-primary-200)',
  },
  [SubscriptionStatus.UNKNOWN]: {
    text: '',
    color: '',
  },
}

const CardStatus = ({ feature }: { feature: PremiumFeatureFromHook }) => {
  let statusInfo = feature?.subscriptionsView?.current
    ? statusesInfo[feature?.subscriptionsView?.current?.status]
    : undefined
  const packagesStatus = feature.packagesStatus

  const isInOpenTrial =
    typeof feature?.openTrialDaysRemaining === 'number' &&
    !Number.isNaN(feature?.openTrialDaysRemaining) &&
    feature?.openTrialDaysRemaining > 0

  // If all packages are available, it means that we want the
  // package in free open trial to show as active
  const allPackagesAreAvailable = packagesStatus?.every(
    (pkg) => pkg.status === SubscriptionStatus.AVAILABLE_FOR_PURCHASE,
  )

  /**
   * We don't want to always show the same status of the subscription
   * in the Status component. For that we have these special rules
   * below.
   */
  const isActiveDueToOpenTrial =
    isInOpenTrial && (!packagesStatus || allPackagesAreAvailable)
  const isCancelled =
    feature?.subscriptionsView?.current?.status === SubscriptionStatus.CANCELLED

  if (isActiveDueToOpenTrial || isCancelled) {
    statusInfo = statusesInfo[SubscriptionStatus.ACTIVE]
  }

  return (
    <Status
      className={FeatureCardIds.Status}
      style={{
        backgroundColor: statusInfo ? statusInfo.color : undefined,
      }}
    >
      <Text textAlign="center" letterSpacing="tight" color="primary-500">
        {statusInfo ? statusInfo.text : undefined}
      </Text>
    </Status>
  )
}

export const FeatureCard = ({
  feature,
  index,
  setIsOpen,
}: {
  feature: PremiumFeatureFromHook
  index: number
  setIsOpen: (isOpen: boolean) => void
}) => {
  return (
    <Card key={feature.id} className={FeatureCardIds.Card}>
      <CardStatus feature={feature} />
      <Content feature={feature} index={index}>
        <StatusOrAction feature={feature} setIsOpen={setIsOpen} />
      </Content>
    </Card>
  )
}
